/**
 * MODULE: _shared/frontend-shell/src/hooks/goldenCrmOpportunityMapper.ts
 * PURPOSE: Map shell CRM deal rows (GET /api/v1/crm/deals) to sales-opportunities Opportunity shape for dashboard widgets
 * PUBLIC_API: mapCrmDealRowToDashboardOpportunity, mapCrmOpportunityToDashboardOpportunity
 * AGENT_SAFETY: SAFE
 */

import type { Opportunity as DashboardOpportunity } from '@domain/sales-opportunities/contracts/types';
import {
  mapSalesOpportunityRowToCrm,
} from '../features/crm/hooks/crmEntityMappers';
import type { Opportunity as CrmOpportunity } from '../features/crm/types/opportunity.types';

function crmStatusFromStage(stage: string): string {
  if (stage === 'closed_won') return 'won';
  if (stage === 'closed_lost') return 'lost';
  return 'active';
}

/** CRM slice type → dashboard contract (same fields legacy sales-opportunities list exposed). */
export function mapCrmOpportunityToDashboardOpportunity(o: CrmOpportunity): DashboardOpportunity {
  const close =
    o.closeDateISO.length >= 10 ? o.closeDateISO.slice(0, 10) : undefined;
  const activityCoverage =
    o.activityCoverage === 'complete' || o.activityCoverage === 'partial'
      ? o.activityCoverage
      : 'unknown';
  return {
    id: o.id,
    tenant_id: '',
    name: o.name,
    value: o.amount,
    stage: o.stage,
    probability: o.probability,
    expected_close_date: close,
    account_name: o.accountName,
    account_id: o.accountId?.trim() || undefined,
    owner_name: o.ownerName,
    owner_id: o.ownerId,
    region: o.territoryId?.trim() || o.territoryName?.trim() || undefined,
    segment: undefined,
    status: crmStatusFromStage(o.stage),
    created_at: o.createdAtISO,
    updated_at: o.updatedAtISO,
    activity_coverage: activityCoverage,
    is_at_risk: o.isAtRisk,
    is_stuck: o.isStuck,
  };
}

/** Raw CRM list row (camel or snake) → dashboard Opportunity. */
export function mapCrmDealRowToDashboardOpportunity(
  row: Record<string, unknown>
): DashboardOpportunity {
  const mapped = mapCrmOpportunityToDashboardOpportunity(mapSalesOpportunityRowToCrm(row));
  const raw = row.currencyCode ?? row.currency_code ?? row.currency;
  if (typeof raw === 'string' && raw.trim().length === 3) {
    mapped.currency = raw.trim().toUpperCase();
  }
  return mapped;
}
